import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import {
  controlBase,
  controlClass,
  cx,
  errorClass,
  fieldHeight,
  hintClass,
  labelClass,
} from './styles';

interface FieldProps {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
  readonly children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children({ id, describedBy })}
      {hint ? (
        <p id={hintId} className={hintClass}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className={errorClass}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

const inputSizes = {
  md: {
    control: controlClass,
    field: `${fieldHeight} px-3`,
    withIcon: 'pl-10',
    icon: 'pl-3.5 [&_svg]:size-4',
  },
  lg: {
    control: `${controlBase} text-base`,
    field: 'h-12 px-4',
    withIcon: 'pl-11',
    icon: 'pl-4 [&_svg]:size-5',
  },
};

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
  readonly icon?: ReactNode;
  readonly size?: keyof typeof inputSizes;
}

export function TextInput({
  label,
  hint,
  error,
  icon,
  size = 'md',
  className,
  ...rest
}: TextInputProps) {
  const sizing = inputSizes[size];
  return (
    <Field label={label} {...(hint ? { hint } : {})} error={error}>
      {({ id, describedBy }) => (
        <div className="group/input relative">
          {icon ? (
            <span
              aria-hidden="true"
              className={cx(
                'pointer-events-none absolute inset-y-0 left-0 flex items-center text-slate-400 transition group-focus-within/input:text-brand-600',
                sizing.icon,
              )}
            >
              {icon}
            </span>
          ) : null}
          <input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            className={cx(sizing.control, sizing.field, icon ? sizing.withIcon : null, className)}
            {...rest}
          />
        </div>
      )}
    </Field>
  );
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
}

export function TextArea({ label, hint, error, className, ...rest }: TextAreaProps) {
  return (
    <Field label={label} {...(hint ? { hint } : {})} error={error}>
      {({ id, describedBy }) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={cx(controlClass, 'min-h-24 px-3 py-2 leading-relaxed', className)}
          {...rest}
        />
      )}
    </Field>
  );
}

export const inlineControl = `${controlClass} ${fieldHeight} min-w-0 px-3`;
